import React from 'react';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { IntlProvider } from 'react-intl';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { composeModules } from 'holocron';
import { compose } from 'redux';
import {
  makeOneServerFetchye,
  OneFetchyeProvider,
  oneCacheSelector,
} from 'fetchye-one-app';
import childRoutes from '../childRoutes';
import reducer, { loadLoyaltyCustomerDetails } from '../ducks';
import { getIsSSREnabled, getPreloadModules } from '../selectors';
import { MODULE_ID } from '../constants';

const AxpLoyaltyExperienceRoot = ({
  languageData,
  locale,
  children,
}) => {
  if (Object.entries(languageData).length === 0) {
    return (
      <div
        role="progressbar"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-label="Large circle indeterminate"
        className="progress-circle progress-indeterminate progress-lg"
      />
    );
  }
  return (
    <IntlProvider locale={locale} messages={languageData}>
      <Helmet
        title="cmv"
        meta={[
          {
            name: 'viewport',
            content:
              'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover',
          },
          {
            name: 'description',
            content: 'CMV',
          },
        ]}
        link={[
          {
            rel: 'stylesheet',
            href: 'https://www.aexp-static.com/cdaas/one/statics/@americanexpress/dls/6.21.5/package/dist/6.21.5/styles/dls.min.css',
          },
          {
            rel: 'preconnect',
            href: 'https://loyalty-mock-dev.aexp.com',
          },
        ]}
      />
      { /* OneFetchyeProvider is configured to use OneCache */ }
      <OneFetchyeProvider>
        <div data-testid="loy-exp-root-container" className="dls-gray-02-bg">
          {children}
        </div>
      </OneFetchyeProvider>
    </IntlProvider>
  );
};

export const TestableAxpLoyaltyPocRoot = AxpLoyaltyExperienceRoot;

AxpLoyaltyExperienceRoot.propTypes = {
  children: PropTypes.node,
  languageData: PropTypes.shape({}).isRequired, // no need to restate all the keys in the lang pack
  locale: PropTypes.string.isRequired,
};

// Read about childRoutes:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/Routing.md#childroutes
AxpLoyaltyExperienceRoot.childRoutes = childRoutes;

// Read about appConfig:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/App-Configuration.md
/* istanbul ignore next */
if (!global.BROWSER) {
  // eslint-disable-next-line global-require -- require needs to be inside browser check
  AxpLoyaltyExperienceRoot.appConfig = require('../config').default;
}

export async function loadModuleData({
  store: { dispatch, getState },
  fetchClient,
  ownProps,
}) {
  try {
    const isSSREnabled = getIsSSREnabled(getState());
    const preloadModules = getPreloadModules(getState(), ownProps);
    const promises = [];
    if (isSSREnabled && !global.BROWSER) {
      const fetchye = makeOneServerFetchye({
        store: { dispatch, getState },
        fetchClient,
        cacheSelector: oneCacheSelector,
      });
      promises.push(loadLoyaltyCustomerDetails(fetchye));
    }
    promises.push(dispatch(loadLanguagePack(MODULE_ID, { fallbackLocale: 'en-US' })));
    promises.push(dispatch(composeModules(preloadModules)));

    await Promise.all(promises);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console -- for debugging
      console.warn(MODULE_ID, error);
    } else {
      /* swallow error */
    }
  }
}

AxpLoyaltyExperienceRoot.holocron = {
  name: MODULE_ID,
  loadModuleData,
  reducer,
};

export default compose(
  oneAppModuleWrapper(MODULE_ID)
)(AxpLoyaltyExperienceRoot);
